import { IEmployee } from "Dashboard/scenes/HumanResources/domain/IEmployee";
import { useEffect, useRef } from "react";
import {
  getAllPayrollsForEmployee,
  getAllPayrollsForEmployeePrint,
  selectState,
  setCalculatePayrollPopover,
} from "../payroll.slice";
import { useAppDispatch } from "hooks/hooks";
import Loader from "components/Loader/Loader";
import { useSelector } from "react-redux";
import DeletePayrollModal from "./DeletePayrollModal";
import CalculatePayrollPopover from "./CalculatePayrollPopover";
import PayrollCard from "./PayrollCard";
import PrintYearlyPayroll from "./PrintYearlyPayroll";
import Popover from "components/Popover/Popover";
import InfoTooltip from "components/Popup/InfoTooltip";
import { useState } from "react";
import { IPayroll } from "Dashboard/scenes/HumanResources/domain/IPayroll";
import { FaCircleArrowDown, FaCircleArrowUp } from "react-icons/fa6";
import ToolboxButton, { ToolboxButtonIcon }
  from "Dashboard/components/Buttons/ToolboxButton";
import { hasWriteAccessTo } from "utils/authUtil";
import { HUMAN_RESOURCES_MODULE_NAME }
  from "Dashboard/scenes/HumanResources/domain/HumanResourcesModule";
import { selectState as selectStateAuth }
  from "authentication/main.slice";
import { usePagePrint } from "hooks/usePagePrint";

interface IProps {
  employee: IEmployee;
}

const PayrollInfo = ({ employee }: IProps) => {
  const componentRef = useRef<HTMLDivElement>(null);

  const [shouldShowFuturePayrolls, setShowFuturePayrolls] = useState(false);

  const dispatch = useAppDispatch();
  const {
    loading,
    payrolls,
    payrollsForPrint,
    calculatePayrollPopover
  } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const handlePrint = usePagePrint(componentRef, { margins: "0.5cm" });

  useEffect(() => {
    if (employee?.employeeId) {
      dispatch(getAllPayrollsForEmployee(employee.employeeId));
      dispatch(getAllPayrollsForEmployeePrint({
        employeeId: employee.employeeId
      }));
    }
  }, [dispatch, employee.employeeId]);

  const currentDate = new Date();
  const hasPayrollsInFuture = payrolls.some(p => {
    return p.year >= currentDate.getUTCFullYear() && p.month > currentDate.getUTCMonth() + 1;
  });

  const toggleFuturePayrolls = () => {
    setShowFuturePayrolls((prevState) => !prevState);
  };

  const showFuturePayrolls = (payroll: IPayroll) => {
    if (shouldShowFuturePayrolls) return true;

    return payroll.year < currentDate.getUTCFullYear() ||
      (payroll.year === currentDate.getUTCFullYear() && payroll.month <= currentDate.getUTCMonth() + 1);
  }

  return (
    <>
      <div className="hr-salary-info">
        <div className="salary-headline">
          <ToolboxButton
            icon={ToolboxButtonIcon.ArrowCircleDown}
            onClick={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
              ? (e) => {
                dispatch(setCalculatePayrollPopover(
                  { isOpen: true, top: e.clientY, left: e.clientX }));
              }
              : undefined}
            tooltip={`Obračun plate za ${employee ? employee.employeeName : "zaposlenog"}`}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip={`Štampa liste obračuna plata poslednje godine za ${employee ? employee.employeeName : "zaposlenog"}`}
            onClick={user?.username === "test" ? handlePrint : undefined}
          />
          <h1>Obračun plata</h1>
        </div>
        <div className="salary-tickets">
          <div className="salary-grid salary-tickets-header">
            <div>obračun</div>
            <div>datum isp.</div>
            <div className="locked-width-105">karnet</div>
            <div></div>
            <div>obroci</div>
            <div>bonus i korekcija</div>
            <div className="locked-width-150">ispl / troš</div>
            <div className="button-future-payrolls">
              {hasPayrollsInFuture &&
                <InfoTooltip
                  onClick={toggleFuturePayrolls}
                  tooltip={shouldShowFuturePayrolls
                    ? 'Sakrij obračune iz budućnosti'
                    : 'Prikaz obračuna iz budućnosti'}
                  element="span"
                >
                  {shouldShowFuturePayrolls
                    ? <FaCircleArrowUp size={15} />
                    : <FaCircleArrowDown size={15} />}
                </InfoTooltip>
              }
            </div>
          </div>
          <div className="salary-container">
            {loading && <Loader />}
            <div>
              {payrolls &&
                payrolls
                  .filter((payroll) => showFuturePayrolls(payroll))
                  .map((payroll, index) => (
                    <PayrollCard
                      employee={employee}
                      payroll={payroll}
                      index={index}
                      key={index}
                    />
                  ))}
            </div>
          </div>
        </div>
      </div>
      <DeletePayrollModal />
      <Popover
        isOpen={calculatePayrollPopover.isOpen}
        onClose={() => dispatch(setCalculatePayrollPopover({ isOpen: false }))}
        title="Dodaj Obračun"
        direction="bottom"
        position={{
          top: calculatePayrollPopover.top,
          left: calculatePayrollPopover.left,
          offsetLeft: 3,
          offsetTop: 20
        }}
      >
        <CalculatePayrollPopover employee={employee} />
      </Popover>
      <div ref={componentRef} className="print-area">
        <PrintYearlyPayroll
          payrolls={payrollsForPrint}
          employee={employee}
        />
      </div>
    </>
  );
};

export default PayrollInfo;
